import { useMemo } from 'react';
import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';

import type { OrderItem } from '../../../graphql/query/getOrder';
import { rawString, Text } from '../../Language/Language';
import {
  eligibleHeader,
  itemSelectionStepId,
} from '../../../layouts/ItemSelection/baseConfig';
import {
  ItemCardButtonView,
  ClaimedItemCard,
} from '../../ItemCard/ItemCardView';
import { ShipmentGroup } from '../../ShipmentGroup/ShipmentGroup';
import type { ShipmentGroup as ShipmentGroupType } from '../../../utils/item';
import { expandItemsWithClaims } from '../../../utils/expandItemsWithClaims';
import s from './ItemSelectionTask.module.css';
import { ShowMoreButton, showMoreCutoff } from './ShowMoreButton';
import type { ItemSelectConfig } from '../../../graphql/query/getRetailerSettings';

export interface EligibleItemsViewProps {
  eligibleItems: OrderItem[];
  hasMultipleShipments: boolean;
  shipmentGroups: ShipmentGroupType[];
  isMobile: boolean;
  showMore: boolean;
  onShowMore: () => void;
  onItemSelect: (itemId: string, trackingNumber?: string) => void;
  onClaimClick?: (claimId: string) => void;
  remainingQuantity?: number;
  itemSelectConfig?: ItemSelectConfig | null;
}

/**
 * Pure presentational component for displaying eligible items.
 * Contains no business logic, hooks, or side effects.
 * All data and callbacks are provided via props.
 */
export const EligibleItemsView = ({
  eligibleItems,
  hasMultipleShipments,
  shipmentGroups,
  isMobile,
  showMore,
  onShowMore,
  onItemSelect,
  onClaimClick,
  remainingQuantity,
  itemSelectConfig,
}: EligibleItemsViewProps) => {
  // Expand items with claims into separate display items
  const displayItems = useMemo(
    () => expandItemsWithClaims(eligibleItems),
    [eligibleItems],
  );

  if (!eligibleItems || eligibleItems.length === 0) {
    return null;
  }

  return (
    <Box className={s.wrapper} padding={['px-24', 'md:px-0']}>
      <Flex alignItems="center" className={s.headerContainer} gap="g-8">
        <h1 className={s[`${itemSelectionStepId}-${eligibleHeader}`]}>
          <Text langKey={`Global.${itemSelectionStepId}.EligibleHeader`} />
        </h1>
        <PopoverRoot>
          <PopoverTrigger asChild={true}>
            <IconButton
              icon="question"
              label={rawString({
                langKey: 'Global.help',
              })}
              variant="tertiary"
            />
          </PopoverTrigger>
          <Popover
            align={isMobile ? 'end' : 'center'}
            ariaLabel={rawString({
              langKey: 'Global.info',
            })}
            hasArrow={false}
          >
            <PopoverContentSimple
              closeButtonLabel={rawString({
                langKey: 'Global.close',
              })}
            >
              <Text langKey="Global.itemSelection.EligibleHeaderHelp" />
            </PopoverContentSimple>
          </Popover>
        </PopoverRoot>
      </Flex>

      {hasMultipleShipments ? (
        <Flex direction="column" gap="g-24">
          {shipmentGroups.map((shipmentGroup) => {
            // Filter eligible items for this shipment
            const eligibleItemsInShipment = shipmentGroup.items.filter((item) =>
              eligibleItems.some((eligibleItem) => eligibleItem.id === item.id),
            );

            if (eligibleItemsInShipment.length === 0) return null;

            return (
              <ShipmentGroup
                key={shipmentGroup.shipmentIndex}
                shipmentGroup={{
                  ...shipmentGroup,
                  items: eligibleItemsInShipment,
                }}
                onItemSelect={(itemId, trackingNumber) =>
                  onItemSelect(itemId, trackingNumber)
                }
                onClaimClick={onClaimClick}
                imageSize={isMobile ? 'sm' : 'xl'}
                showShipmentDetails={true}
                showMoreCutoff={showMoreCutoff}
                itemSelectConfig={itemSelectConfig}
              />
            );
          })}
        </Flex>
      ) : (
        <Flex direction="column" gap="g-16">
          {displayItems.map((displayItem, i) => {
            if (!showMore && i > showMoreCutoff - 1) return null;

            if (displayItem.isClaimedItem && displayItem.singleClaim) {
              return (
                <ClaimedItemCard
                  key={displayItem.displayId}
                  displayItem={displayItem}
                  imageSize={isMobile ? 'sm' : 'xl'}
                  itemSelectConfig={itemSelectConfig}
                  onClaimClick={onClaimClick}
                  ariaLabelledby={`${displayItem.displayId}-title`}
                />
              );
            }

            return (
              <ItemCardButtonView
                key={displayItem.displayId}
                onClick={() => onItemSelect(displayItem.id)}
                aria-labelledby={`${displayItem.displayId}-title`}
                imageSize={isMobile ? 'sm' : 'xl'}
                item={displayItem}
                remainingQuantity={
                  remainingQuantity || displayItem.quantity?.remaining || 1
                }
                isShipmentScopeReason={false}
                itemSelectConfig={itemSelectConfig}
              />
            );
          })}
        </Flex>
      )}

      {!hasMultipleShipments && (
        <ShowMoreButton
          onClick={onShowMore}
          showMore={showMore}
          hiddenItemCount={displayItems.length - showMoreCutoff}
        />
      )}
    </Box>
  );
};
